import { formatClock, type QueryView } from '@krp/client-runtime'

import { aqsiRegisterHealth, cabinetReadHealth } from './registerHealth'
import type {
  AqsiRegister,
  AqsiRegisterLink,
  AqsiRegisters,
  RegisterKind,
  RegisterPrimary,
  RegisterRowVm,
  RmkRights,
  ShiftsBoardVm,
} from './types'

/** Порядок в списке касс «Начала работы»: доступные (свободные, затем с открытой свободной сменой) → занятые → не на связи → нельзя. */
const KIND_ORDER: Readonly<Record<RegisterKind, number>> = { free: 0, continue: 1, occupied: 2, unreachable: 3, unavailable: 4 }

const WHO_NOBODY = 'Никто не работает'

function shiftTextOf(register: AqsiRegister): string | null {
  if (register.shiftOpen !== true) return null
  const number = register.shiftNumber === null ? 'Смена открыта' : `Смена №${register.shiftNumber}`
  const since = formatClock(register.shiftOpenedAt)
  return since === null || register.shiftNumber === null ? number : `${number} · с ${since}`
}

/** Кто открыл смену — только если служба знает; про «программу» и «кассу» говорим словами кассира. */
function openerText(register: AqsiRegister): string | null {
  if (register.shiftOpenedByMe) return 'Вы открыли эту смену раньше'
  if (register.shiftOpenedBy === null) return null
  return register.shiftOpenedBySource === 'cabinet'
    ? `Смену открыл(а) на самой кассе: ${register.shiftOpenedBy}`
    : `Смену открыл(а) ${register.shiftOpenedBy}`
}

function kindOf(register: AqsiRegister): RegisterKind {
  switch (register.state) {
    case 'occupied':
      return 'occupied'
    case 'unreachable':
      return 'unreachable'
    case 'unavailable':
      return 'unavailable'
    case 'opened-outside':
      return 'continue'
    default:
      return 'free'
  }
}

const RELEASE_UNAVAILABLE =
  'Освободить эту кассу отсюда пока нельзя: служба не сообщает, чью работу закрывать. Попросите сотрудника нажать «Закончить работу».'

function rowOf(
  register: AqsiRegister,
  links: Readonly<Record<number, AqsiRegisterLink | undefined>>,
  rights: RmkRights,
  busy: boolean,
): RegisterRowVm {
  const kind = kindOf(register)
  const health = aqsiRegisterHealth(links[register.deviceId], { deviceId: register.deviceId, name: register.name }).register
  const since = formatClock(register.occupiedSince)
  const shiftText = shiftTextOf(register)
  const base = {
    deviceId: register.deviceId,
    name: register.name,
    kind,
    shiftText,
    health,
    canRelease: false,
    selectable: false,
    releaseUnavailableReason: null as string | null,
    foreignSession:
      register.foreignSessionId !== null && register.foreignSessionVersion !== null
        ? { sessionId: register.foreignSessionId, version: register.foreignSessionVersion }
        : null,
    canJournal: rights.journal,
  }
  switch (kind) {
    case 'occupied': {
      const who = register.occupiedBy ?? 'другой сотрудник'
      const seen = formatClock(register.occupiedLastSeenAt)
      const shift = register.shiftNumber !== null ? `, смена №${register.shiftNumber}` : register.shiftOpen === true ? ', смена открыта' : ', смена не открыта'
      return {
        ...base,
        headline: `Занята: работает ${who}${since === null ? '' : ` с ${since}`}${shift}`,
        whoText: `Работает ${who}${since === null ? '' : ` с ${since}`}`,
        detail:
          `Недоступна: за кассой ${who}.` +
          (seen === null ? '' : ` Он был на связи в программе в ${seen}.`) +
          ' Дождитесь окончания его работы.',
        primary: null,
        primaryEnabled: false,
        canRelease: rights.takeover || rights.closeAny,
        releaseUnavailableReason:
          register.foreignSessionId !== null && register.foreignSessionVersion !== null ? null : RELEASE_UNAVAILABLE,
      }
    }
    case 'unreachable': {
      const noAnswer = formatClock(register.connection?.noAnswerSince ?? register.lastContactAt)
      return {
        ...base,
        headline: `Касса не отвечает${noAnswer === null ? '' : ` с ${noAnswer}`}`,
        whoText: WHO_NOBODY,
        detail: 'Включите кассу, проверьте заряд и Wi-Fi или SIM, затем нажмите «Проверить связь».',
        primary: 'check',
        primaryEnabled: !busy,
      }
    }
    case 'unavailable':
      return {
        ...base,
        headline: 'Работать нельзя',
        whoText: '',
        detail: register.reason ?? (register.message !== '' ? register.message : null),
        primary: null,
        primaryEnabled: false,
      }
    case 'continue': {
      const opener = openerText(register)
      return {
        ...base,
        headline: `${register.shiftNumber === null ? 'Смена' : `Смена №${register.shiftNumber}`} открыта на кассе · никто не работает`,
        whoText: WHO_NOBODY,
        detail: opener,
        primary: register.canStart ? 'continue' : null,
        primaryEnabled: register.canStart && !busy,
        selectable: register.canStart,
      }
    }
    default: {
      const unread = register.shiftOpen === null
      const primary: RegisterPrimary = register.canStart ? 'open' : null
      return {
        ...base,
        headline: unread ? 'Свободна · смена не прочитана' : 'Свободна · смена закрыта',
        whoText: WHO_NOBODY,
        detail: !register.canStart
          ? register.reason
          : unread
            ? 'Состояние смены не прочитано у кабинета — нажмите «Обновить».'
            : null,
        primary,
        primaryEnabled: register.canOpenShift && !busy,
        selectable: primary !== null && register.canOpenShift,
      }
    }
  }
}

/** Список касс «Начала работы» из живого списка касс магазина источника. */
export function buildBoard(
  view: QueryView,
  data: AqsiRegisters | undefined,
  links: Readonly<Record<number, AqsiRegisterLink | undefined>>,
  rights: RmkRights,
  busy: boolean,
): ShiftsBoardVm {
  const ready = view.status === 'ready' || view.status === 'empty'
  // Моя касса на списке не показывается: при своей работе окно открывает порог смены или продажу (А1).
  const registers = ready && data !== undefined ? data.registers.filter((register) => register.state !== 'mine') : []
  const rows = registers
    .map((register) => ({ row: rowOf(register, links, rights, busy), register }))
    .sort((a, b) => KIND_ORDER[a.row.kind] - KIND_ORDER[b.row.kind] || a.row.name.localeCompare(b.row.name, 'ru'))
  // Плитки открытых смен: «ваша смена» первой (А5), затем свободные для продолжения, затем недоступные.
  const tiles = rows
    .filter(({ register }) => register.shiftOpen === true)
    .sort(
      (a, b) =>
        Number(b.register.shiftOpenedByMe) - Number(a.register.shiftOpenedByMe) ||
        KIND_ORDER[a.row.kind] - KIND_ORDER[b.row.kind],
    )
    .map(({ row }) => row)
  const all = rows.map(({ row }) => row)
  return {
    tiles,
    rows: all,
    freeCount: all.filter((row) => row.kind === 'free').length,
    total: all.length,
    cabinet: cabinetReadHealth(view, data?.readAt ?? null),
    hasOpenShifts: tiles.length > 0,
    readAtText: ready ? formatClock(data?.readAt ?? null) : null,
  }
}
